/* eslint-disable max-lines */
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  faLayerGroup,
  faChevronDown,
  faChevronUp,
  faMap,
  faHouseFloodWater,
  faXmark,
  faCloudUpload,
} from '@fortawesome/pro-solid-svg-icons';
import {
  Icon,
  Button,
  Toggle,
  ScrollContainer,
} from '@nazka/nazka.mapframe.components';

import CompareModeLayerPanelFloodSection from '@App/components/CompareModeLayerPanel/CompareModeLayerPanelFloodSection';
import FloodDamagesSelector from '@App/components/LayerSelector/FloodDamagesSelector/FloodDamagesSelector';
import { useFloodDamages } from '@utils/queries/calculation.queries';
import { useUserSettings, useUpdateUserSettingsMutation, useUserAreas } from '@utils/queries/user.queries';
import { setUploadModalStatus } from '@App/components/LayerSelector/layer-selector.redux.action';
import { usePrev } from '@services/hooks.service';

import UrbanAtlasCover from './UrbanAtlasCover/UrbanAtlasCover';
import UrbanAtlasWMSLayer from '../UrbanAtlasWMSLayer/UrbanAtlasWMSLayer';

import './layer-selector.styl';
import FlashFloodDamageSelector from './FlashFloodDamageSelector/FlashFloodDamageSelector';
import UploadModal from '../UploadModal/UploadModal';
import BuildingRadios from './BuildingsRadios/BuildingRadios';
import FloodsRadios from './FloodsRadios/FloodsRadios';
import { useUploadedBuildingsRecords, useUploadedFloodsRecords } from '../../../utils/queries/upload.queries';

function LayerSelector() {
  const [isOpen, setIsOpen] = useState(true);
  const [uploadType, setuploadType] = useState('')
  const [isFloodScenarioExpanded, setIsFloodScenarioExpanded] = useState(true)
  const [isLanduseExpanded, setIsLanduseExpanded] = useState(true)

  const { data: floodDamages } = useFloodDamages();
  const { data: userSettings, isLoading: isLoadingUserSettings } = useUserSettings();
  const { data: userAreas } = useUserAreas();

  const {
    activeLandUseFeature,
    isFloodLayerActive,
  } = userSettings || {};

  const map = useSelector(state => state.maplibreMap.map);
  const isUploadModalOn = useSelector(state => state.layerSelector.isUploadModalOn)
  const isCompareModeOn = useSelector(state => state.buildingDamages.isComparisonModeOn)
  const showData = useSelector(state => state.t1.showData);
  const isCreatingNewDamageLayer = useSelector(state => state.newDamageLayer.isCreatingNewDamageLayer);
  const currentSelectedDamageLayer = useSelector(state => state.layerSelector.currentSelectedDamageLayer);
  const userSelectedMode = useSelector(state => state.maplibreMap.userSelectedMode);

  const prevSelectedCurrentSelectedDamageLayer = usePrev(currentSelectedDamageLayer);
  const { data: floodRecords } = useUploadedFloodsRecords()
  const { data: buildingRecords } = useUploadedBuildingsRecords()

  const dispatch = useDispatch();

  const { mutate: updateUserSettings } = useUpdateUserSettingsMutation();

  useEffect(() => {
    if (showData) {
      setIsOpen(false)
    }
  }, [showData])

  useEffect(() => {
    if (currentSelectedDamageLayer && !prevSelectedCurrentSelectedDamageLayer) {
      updateUserSettings({
        activeLandUseFeature: null,
      });
    }
  }, [currentSelectedDamageLayer, prevSelectedCurrentSelectedDamageLayer, updateUserSettings]);

  return (
    <div className="layer-selector">
      <div className={`z-depth-1 layer-selector__container 
        ${isLoadingUserSettings ? ' is-loading' : ''} ${isOpen ? '' : 'layer-selector__container--close'}`}
      >
        <ScrollContainer
          maxHeight={265}
          css={{
            width: '270px',
          }}
        >
          {userSelectedMode && userSelectedMode === 'River flood' && (
            <div className={`layer-selector__body__wrapper ${isOpen ? 'expanded' : ''}`}>
              <hr style={{
                opacity: 0.1, margin: '0 10px',
              }}
              />
              <div className="layer-selector__header__text__layer__wrapper">
                <Icon icon={faHouseFloodWater} size="18px" />
                <div className="layer-selector__flood__title-wrapper">
                  <span>Flood scenario</span>
                  <Button
                    layout="ghost"
                    hover={false}
                    icon={faCloudUpload}
                    css={{
                      color: 'grey',
                      marginLeft: 0,
                      padding: 0,
                      '&:hover': {
                        color: 'black',
                      },
                      '&:disabled': {
                        backgroundColor: 'transparent',
                        border: 'none',
                        '&:hover': {
                          color: 'grey',
                        },
                      },
                    }}
                    onClick={() => {
                      setuploadType('flood')
                      dispatch(setUploadModalStatus(true))
                      updateUserSettings({ isFloodLayerActive: true })
                    }}
                    disabled={floodRecords?.length === 3}
                    rootElProps={{
                      title: floodRecords?.length === 3 ? 'You have reached the limit of 3 uploads, please delete a layer to proceed.'
                        : 'upload custom flood extent',
                    }}
                  />
                </div>
                
                <Button
                  icon={isFloodScenarioExpanded ? faChevronUp : faChevronDown}
                  size="20px"
                  onClick={() => setIsFloodScenarioExpanded(prevState => !prevState)}
                  color="white"
                  hover={false}
                  css={{
                    marginLeft: 'auto',
                  }}
                />
              </div>
              {isFloodScenarioExpanded && (
              <div className="layer-selector__flood__scenario">
                <Toggle
                  label={isCompareModeOn ? 'Flood comparison' : 'Flood extents'}
                  name="flood"
                  checked={isFloodLayerActive}
                  onInputChange={e => updateUserSettings({ isFloodLayerActive: e.target.checked })}
                  activeColor="#0367A5"
                  css={{
                    fontSize: '14px',
                    input: { fontSize: '12px' },
                  }}
                />
                {isCompareModeOn ? (
                  <CompareModeLayerPanelFloodSection />
                ) : (
                  <div>
                    {isFloodLayerActive && <FloodsRadios map={map} />} 
                  </div>
                )}

              </div>
              )}

            </div>
          )}
          
          <div>
            <hr style={{
              opacity: 0.1, margin: '0 10px',
            }}
            />
            <div className="layer-selector__header__text__layer__wrapper">
              <Icon icon={faMap} size="16px" />
              <span>Land use features</span>
              <Button
                icon={isLanduseExpanded ? faChevronUp : faChevronDown}
                size="20px"
                onClick={() => setIsLanduseExpanded(prevState => !prevState)}
                color="white"
                hover={false}
                css={{
                  marginLeft: 'auto',
                }}
              />
            </div>
            {isLanduseExpanded && (
              <div>
                <UrbanAtlasCover />
                <div className="layer-selector__building-data__wrapper">
                  <Toggle
                    label="Building data"
                    name="building data"
                    checked={activeLandUseFeature === 'Building data'}
                    activeColor="#0367A5"
                    onInputChange={() => updateUserSettings({
                      activeLandUseFeature: `${activeLandUseFeature === 'Building data' ? null : 'Building data'}`,
                    })}
                    css={{
                      fontSize: '14px',
                      input: { fontSize: '12px' },
                      // marginBottom: '6px',
                    }}
                  />
                  <Button
                    layout="ghost"
                    hover={false}
                    icon={faCloudUpload}
                    css={{
                      color: 'grey',
                      marginLeft: 0,
                      padding: 0,
                      '&:hover': {
                        color: 'black',
                      },
                      '&:disabled': {
                        backgroundColor: 'transparent',
                        border: 'none',
                        '&:hover': {
                          color: 'grey',
                        },
                      },
                    }}
                    onClick={() => {
                      setuploadType('building')
                      dispatch(setUploadModalStatus(true))
                      updateUserSettings({ activeLandUseFeature: 'Building data' })
                    }}
                    disabled={buildingRecords?.length === 3}
                    rootElProps={{
                      title: buildingRecords?.length === 3 ? 'You have reached the limit of 3 uploads, please delete a layer to proceed.'
                        : 'upload custom building data',
                    }}
                  />
                </div>
                
                <div className="layer-selector__land-use-features__building__options">
                  {activeLandUseFeature === 'Building data' && (
                    <BuildingRadios />
                  )}
                </div>
              </div>
            )}

          </div>
          {userAreas && floodDamages && !isCreatingNewDamageLayer && userSelectedMode && (
            <>
              <hr style={{ opacity: 0.1, margin: '0 10px' }} />
              {userSelectedMode === 'River flood' ? (
                <FloodDamagesSelector map={map} />
              ) : (
                <FlashFloodDamageSelector map={map} />
              ) }
            </>
          )}
        </ScrollContainer>
      </div>
      <div>
        <Button
          icon={isOpen ? faXmark : faLayerGroup}
          onClick={() => setIsOpen(prevState => !prevState)}
          color="white"
          hover
          css={{
            borderRadius: '3px',
          }}
        />
      </div>
      {map && activeLandUseFeature === 'Urban Atlas Cover' && (
        <UrbanAtlasWMSLayer map={map} />
      )}
      {isUploadModalOn && (
        <UploadModal uploadType={uploadType} />
      )}
    </div>
  )
}

export default LayerSelector